import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, Percent, CheckCircle2, ArrowRight, ChevronDown, Coins, Fuel } from 'lucide-react';
import CryptoAffiliateBlock from '../../CryptoAffiliateBlock';
import RiskAnalysisBlock from '../../RiskAnalysisBlock';

const EthereumProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  // Real-time calculation on input change
  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investmentAmount]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const investment = parseFloat(investmentAmount);

    if (isNaN(buy) || isNaN(sell) || isNaN(investment) || buy <= 0 || investment <= 0) {
      setResult(null);
      return;
    }

    // Calculate ETH amount purchased
    const ethAmount = investment / buy;
    
    // Calculate final value
    const finalValue = ethAmount * sell;
    
    // Calculate profit/loss
    const profitLoss = finalValue - investment;
    
    // Calculate percentage gain/loss
    const percentageGain = ((sell - buy) / buy) * 100;
    
    // ROI based on investment
    const roi = ((finalValue - investment) / investment) * 100;

    setResult({
      ethAmount,
      finalValue,
      profitLoss,
      percentageGain,
      roi,
      isProfitable: profitLoss > 0,
      isBreakEven: Math.abs(profitLoss) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestmentAmount('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatETH = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 }) + ' ETH';
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return prefix + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  };

  // Quick example scenarios - ETH specific
  const scenarios = [
    { label: '$1.5K → $2.5K', buy: '1500', sell: '2500', investment: '1000' },
    { label: '$2K → $4K', buy: '2000', sell: '4000', investment: '2500' },
    { label: '$3K → $5K', buy: '3000', sell: '5000', investment: '5000' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestmentAmount(scenario.investment);
  };

  // Coin options for dropdown
  const coinOptions = [
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', current: true },
    { name: 'All Cryptocurrencies', slug: 'crypto-profit-calculator', icon: '🪙', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="ethereum-profit-calculator">
      {/* Coin Selector Dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 border border-indigo-500/30 rounded-lg text-indigo-400 hover:bg-indigo-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl">Ξ</span>
          <span className="font-medium">Ethereum (ETH)</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-indigo-500/10 text-indigo-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator Input Section */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
            <span className="text-2xl text-indigo-400">Ξ</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Ethereum Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your ETH investment returns instantly</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 1500"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">ETH price when you bought</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 2500"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Current or target sell price</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                placeholder="e.g., 1000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Total USD invested</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
          data-testid="clear-btn"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Main Result - Profit/Loss */}
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? (
                  <TrendingUp className="text-green-400" size={28} />
                ) : result.isBreakEven ? (
                  <DollarSign className="text-yellow-400" size={28} />
                ) : (
                  <TrendingDown className="text-red-400" size={28} />
                )}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Profit' : result.isBreakEven ? 'Break Even' : 'Loss'}
                </span>
              </div>
              <button
                onClick={() => copyResult(result.profitLoss, 'profit')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.profitLoss >= 0 ? '+' : ''}{formatCurrency(result.profitLoss)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.percentageGain)} gain
              </p>
            </div>
          </div>

          {/* Detailed Results Grid */}
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">ETH Purchased</p>
              <p className="text-lg font-semibold text-indigo-400">{formatETH(result.ethAmount)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Investment</p>
              <p className="text-lg font-semibold text-white">{formatCurrency(parseFloat(investmentAmount))}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Final Value</p>
              <p className="text-lg font-semibold text-white">{formatCurrency(result.finalValue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">ROI</p>
              <p className={`text-lg font-semibold ${result.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)}
              </p>
            </div>
          </div>

          {/* Calculation Breakdown */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Calculator size={16} className="text-indigo-400" />
              Calculation Breakdown
            </h4>
            <div className="space-y-2 text-sm font-mono">
              <p className="text-gray-400">
                ETH Amount = ${investmentAmount || '0'} ÷ ${buyPrice || '0'} = <span className="text-indigo-400">{result.ethAmount.toFixed(6)} ETH</span>
              </p>
              <p className="text-gray-400">
                Final Value = {result.ethAmount.toFixed(6)} × ${sellPrice || '0'} = <span className="text-white">{formatCurrency(result.finalValue)}</span>
              </p>
              <p className="text-gray-400">
                Profit/Loss = {formatCurrency(result.finalValue)} - ${investmentAmount || '0'} = <span className={result.profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}>{result.profitLoss >= 0 ? '+' : ''}{formatCurrency(result.profitLoss)}</span>
              </p>
            </div>
          </div>

          {/* Gas Fee Note */}
          <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 flex items-start gap-3">
            <Fuel size={18} className="text-orange-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-400">
              <span className="text-orange-400 font-medium">Note:</span> This calculation does not include Ethereum gas fees. Gas costs vary based on network congestion and can significantly impact profits on smaller trades. Consider using Layer 2 solutions for lower fees.
            </p>
          </div>
          
          {/* Risk Analysis Block - NEW CRO ELEMENT */}
          <RiskAnalysisBlock 
            investmentAmount={parseFloat(investmentAmount) || 0}
            currentValue={result.finalValue}
            profit={result.profitLoss}
          />
          
          {/* Affiliate CTA Block */}
          <CryptoAffiliateBlock 
            profit={result.profitLoss} 
            cryptoName="ETH"
            toolName="Ethereum Profit Calculator"
            toolSlug="ethereum-profit-calculator"
            investmentAmount={parseFloat(investmentAmount) || 0}
          />
        </div>
      )}

      {/* Related Crypto Tools - Internal Links */}
      <div className="p-5 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl border border-indigo-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-indigo-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/tools/bitcoin-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-orange-400 transition-colors">Bitcoin Calculator</p>
              <p className="text-sm text-gray-400">BTC profit</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400 transition-colors">Crypto Profit</p>
              <p className="text-sm text-gray-400">All coins</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-roi-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-blue-400 transition-colors">Crypto ROI</p>
              <p className="text-sm text-gray-400">Returns %</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-tax-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-green-400 transition-colors">Crypto Tax</p>
              <p className="text-sm text-gray-400">Estimate tax</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400 transition-colors" />
          </Link>
        </div>
      </div>

      {/* Formula Reference */}
      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
          <Calculator size={16} className="text-purple-400" />
          Ethereum Profit Formula
        </h4>
        <div className="bg-gray-900 p-4 rounded-lg">
          <code className="text-purple-400 text-sm">
            Profit = (Sell Price - Buy Price) × Investment Amount / Buy Price
          </code>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          This formula calculates your net Ethereum profit based on the price difference and your investment amount.
        </p>
      </div>

      {/* Tips Section - ETH Specific */}
      <div className="p-5 bg-gradient-to-r from-green-500/10 to-teal-500/10 rounded-xl border border-green-500/20">
        <h4 className="text-lg font-semibold text-white mb-3">💡 Tips to Maximize Ethereum Profit</h4>
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Track ETH/BTC ratio:</strong> Monitor Ethereum's performance relative to Bitcoin for better entry points</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Use Dollar-Cost Averaging:</strong> Invest fixed amounts regularly to average out volatility</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Monitor gas fees:</strong> Execute trades during low-congestion periods to save on transaction costs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Consider staking:</strong> Earn passive yield on your ETH holdings through proof-of-stake</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Watch network upgrades:</strong> Major Ethereum updates often impact price positively</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EthereumProfitCalculatorTool;
